import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus } from 'lucide-react';
import type {
  EligibleTournament,
  MessageThreadSummary,
} from '@swiss-magic/shared-types';
import { AppHeader } from '@/components/AppHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getEligibleTournaments, getMessageThreads } from '@/lib/api/messages';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast } from '@/lib/toast';

export function MessageCenterPage() {
  const { t } = useTranslation('player');
  const navigate = useNavigate();

  const { data, loading, message } = usePolledData<MessageThreadSummary[]>(
    () =>
      getMessageThreads()
        .then((r) => r.threads)
        .catch((err: unknown) => {
          throw err instanceof ApiError ? new Error(err.userMessage) : err;
        }),
    [],
  );
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  const threads = data ?? [];

  const [pickerOpen, setPickerOpen] = useState(false);
  const [eligible, setEligible] = useState<EligibleTournament[] | null>(null);

  async function openPicker() {
    setPickerOpen(true);
    setEligible(null);
    try {
      const r = await getEligibleTournaments();
      setEligible(r.tournaments);
    } catch (err) {
      setEligible([]);
      if (err instanceof ApiError) showErrorToast(err.userMessage);
    }
  }

  return (
    <div data-testid="message-center-page" className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold">{t('messages.title')}</h2>
          <Button data-testid="new-message-button" onClick={openPicker}>
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            {t('messages.new_message')}
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground">{t('messages.loading')}</p>
        ) : threads.length === 0 ? (
          <p data-testid="messages-empty" className="text-muted-foreground">
            {t('messages.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {threads.map((thread) => (
              <Card
                key={thread.id}
                data-testid="conversation-row"
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => navigate(`/player/messages/${thread.id}`)}
              >
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{thread.tournamentName}</p>
                    {thread.lastMessagePreview && (
                      <p className="truncate text-sm text-muted-foreground">
                        {thread.lastMessagePreview}
                      </p>
                    )}
                  </div>
                  {thread.unreadCount > 0 && (
                    <Badge data-testid="conversation-unread" variant="default">
                      {thread.unreadCount}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent data-testid="new-message-picker">
          <DialogHeader>
            <DialogTitle>{t('messages.picker.title')}</DialogTitle>
          </DialogHeader>
          {eligible === null ? (
            <p className="text-sm text-muted-foreground">{t('messages.loading')}</p>
          ) : eligible.length === 0 ? (
            <p
              data-testid="picker-empty"
              className="text-sm text-muted-foreground"
            >
              {t('messages.picker.empty')}
            </p>
          ) : (
            <ul className="divide-y">
              {eligible.map((e) => (
                <li key={e.tournamentId}>
                  <button
                    type="button"
                    data-testid="picker-option"
                    className="w-full px-2 py-3 text-left text-sm hover:bg-muted/50"
                    onClick={() => {
                      setPickerOpen(false);
                      navigate(
                        `/player/messages/new?tournamentId=${encodeURIComponent(
                          e.tournamentId,
                        )}`,
                      );
                    }}
                  >
                    {e.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
